import * as React from 'react';
import { Dialog } from '@headlessui/react';
import { useSpring, animated, config } from '@react-spring/web';
import { useCallback, useEffect, useRef } from 'react';
import { useGesture } from '@use-gesture/react';
import { ModalOverlay } from '..';
import { ModalVariant } from '../custom-modals';

import { cn } from '@restworld/utils-common';
export interface ModalMobileProps {
  variant?: ModalVariant;
  /**
   * Custom Content Element
   */
  children: React.ReactNode;

  /**
   * Whether is open
   */
  isOpen: boolean;

  /**
   * Callback when to close it
   */
  onClose: () => void;
  /**
   * halts gesture manually
   */
  haltGestures?: boolean;
}

const AnimatedModal = animated(Dialog);

export function ModalMobile({ variant = 'primary', children, isOpen, onClose, haltGestures }: ModalMobileProps) {
  const height = 646;
  const [{ y }, api] = useSpring(() => ({ y: height }));

  const wHeight = useRef(height / 2);

  useEffect(() => {
    wHeight.current = window.innerHeight;
  }, []);

  const bindGesture = useGesture(
    {
      onDrag: ({ last, velocity: [, vy], direction: [, dy], movement: [, my], canceled }) => {
        // when the user releases the sheet, we check whether it passed
        // the threshold for it to close, or if we reset it to its open positino
        if (last) {
          my > height * 0.5 || (vy > 0.5 && dy > 0) ? closeModal(vy) : openModal({ canceled });
        }
        // when the user keeps dragging, we just move the sheet according to
        // the cursor position
        else api.start({ y: my, immediate: true });
      },
      onScroll: ({ delta: [, dy] }) => {
        api.start({
          y: Math.max(-height + wHeight.current, Math.min(y.get() - dy * 3, 0)),
          config: config.stiff,
          immediate: true,
        });
      },
    },
    {
      drag: {
        from: () => [0, y.get()],
        filterTaps: true,
        bounds: { top: 0 },
        rubberband: true,
        delay: 300,
      },
      scroll: {
        bounds: { top: wHeight.current, bottom: 0 },
      },
    }
  );

  const openModal = useCallback(
    ({ canceled }: { canceled?: boolean }) => {
      // when cancel is true, it means that the user passed the upwards threshold
      // so we change the spring config to create a nice wobbly effect
      api.start({
        y: 0,
        immediate: false,
        config: canceled ? config.wobbly : config.stiff,
      });
    },
    [api]
  );

  const closeModal = useCallback(
    (velocity = 0) => {
      api.start({
        y: height,
        immediate: false,
        config: { ...config.stiff, velocity },
      });
    },
    [api]
  );

  useEffect(() => {
    if (isOpen) {
      openModal({ canceled: false });
    }
  }, [isOpen, openModal]);

  const display = y.to((py) => (py < height ? 'flex' : 'none'));

  return isOpen ? (
    <>
      <ModalOverlay show={isOpen} onClick={onClose} />
      <StyledModal
        variant={variant}
        open={isOpen}
        onClose={onClose}
        {...(haltGestures ? {} : bindGesture())}
        style={{
          display,
          y,
        }}
      >
        <StyledModalCard className="modal-card">{children}</StyledModalCard>
      </StyledModal>
    </>
  ) : null;
}

const variantStyles: Record<ModalVariant, string> = {
  primary: '',
  secondary: 'overflow-y-auto',
  tertiary: '',
};

const StyledModal = ({ variant, className, ...props }: any) => (
  <AnimatedModal
    className={cn(
      'flex flex-col sm:bottom-0 xs:bottom-0 fixed top-[10vh] h-[90vh] w-screen z-[160] items-center max-w-none touch-none',
      variantStyles[variant as ModalVariant],
      '[&>div:nth-child(2)]:md:h-full [&>div:nth-child(2)]:md:p-0',
      '[&>div:nth-child(2)>div:nth-child(2)]:rounded-none [&>div:nth-child(2)>div:nth-child(2)]:shadow-none [&>div:nth-child(2)>div:nth-child(2)]:bg-none',
      className
    )}
    {...props}
  />
);

const StyledModalCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col w-[90vw] max-w-[26rem] h-full rounded-t-3xl items-center shadow bg-white overflow-scroll	touch-pan-y	 pb-[48px]',
      className
    )}
    {...props}
  />
);

export default ModalMobile;
